import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../services/i18n/language';
import { TnvedHeadingService, TnvedHeadingSearchResult, TnvedResolveResult } from '../../services/api/tnved-heading';

@Component({
  selector: 'app-tnved',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tnved.component.html',
  styleUrl: './tnved.component.scss'
})
export class TnvedComponent implements OnInit, OnDestroy {
  /** Временно скрыто: кэш подбора кода выключен на бэкенде (флаг RESOLVE_CACHE_ENABLED). */
  readonly cacheUiEnabled = false;

  activeTab: 'categories' | 'code' = 'categories';

  // Вкладка «Поиск категорий»
  query = '';
  results: TnvedHeadingSearchResult[] = [];
  loading = false;
  searched = false;
  error = false;

  // Вкладка «Поиск кода ТН ВЭД»
  productName = '';
  productDescription = '';
  refresh = false;
  resolving = false;
  resolveError = false;
  resolveResult: TnvedResolveResult | null = null;

  titleText = 'Поиск по справочнику ТН ВЭД';
  subtitleText = 'Введите название товара: покажем 20 ближайших по смыслу товарных позиций (4 знака)';
  placeholderText = 'Например: литейные поддоны';
  searchText = 'Найти';
  searchingText = 'Ищем…';
  columnCodeText = 'Код';
  columnLevelText = 'Уровень';
  columnNameText = 'Наименование';
  columnSimilarityText = 'Близость';
  levelChapterText = 'Глава';
  levelHeadingText = 'Позиция';
  emptyText = 'Ничего не найдено';
  errorText = 'Не удалось выполнить поиск. Проверьте, что вы вошли в систему, и попробуйте ещё раз';
  tabCategoriesText = 'Поиск категорий';
  tabCodeText = 'Поиск кода ТН ВЭД';
  subtitleCodeText = 'Введите название и описание товара: определим полный код ТН ВЭД';
  nameLabelText = 'Название товара';
  descriptionLabelText = 'Описание товара';
  descriptionPlaceholderText = 'Например: используется для производства ЖБИ';
  resolveText = 'Определить код';
  resolvingText = 'Определяем…';
  resultCodeText = 'Код ТН ВЭД';
  resultProductText = 'ИИ определил товар как';
  sourceAiText = 'Определён ИИ по подсказкам из справочника';
  sourceTreeText = 'Найден спуском по дереву ТН ВЭД';
  notFullText = 'Полный код определить не удалось — показан родительский';
  notFoundText = 'Код определить не удалось';
  hintsTitleText = 'Подсказки, отправленные ИИ (ближайшие категории)';
  refreshText = 'Определить заново (не брать из кэша)';
  cachedText = 'Из кэша: один и тот же товар всегда даёт один и тот же код';

  private subscriptions = new Subscription();

  constructor(
    private tnvedHeadingService: TnvedHeadingService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onSearch(): void {
    const query = this.query.trim();
    if (!query || this.loading) return;

    this.loading = true;
    this.error = false;
    this.subscriptions.add(
      this.tnvedHeadingService.search(query, 20).subscribe({
        next: (results) => {
          this.results = results;
          this.searched = true;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('TNVED heading search failed:', err);
          this.results = [];
          this.searched = true;
          this.error = true;
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onResolve(): void {
    const name = this.productName.trim();
    if (!name || this.resolving) return;

    this.resolving = true;
    this.resolveError = false;
    this.resolveResult = null;
    this.subscriptions.add(
      this.tnvedHeadingService.resolve(name, this.productDescription.trim(), this.refresh).subscribe({
        next: (result) => {
          this.resolveResult = result;
          this.resolving = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('TNVED resolve failed:', err);
          this.resolveError = true;
          this.resolving = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  sourceText(source: string): string {
    return source === 'AI' ? this.sourceAiText : this.sourceTreeText;
  }

  levelText(level: number): string {
    return level === 2 ? this.levelChapterText : this.levelHeadingText;
  }

  private t(key: string, fallback: string): string {
    return this.languageService.translate(key) || fallback;
  }

  private updateTranslations(): void {
    this.titleText = this.t('tnvedSearch.title', this.titleText);
    this.subtitleText = this.t('tnvedSearch.subtitle', this.subtitleText);
    this.placeholderText = this.t('tnvedSearch.placeholder', this.placeholderText);
    this.searchText = this.t('tnvedSearch.search', this.searchText);
    this.searchingText = this.t('tnvedSearch.searching', this.searchingText);
    this.columnCodeText = this.t('tnvedSearch.columnCode', this.columnCodeText);
    this.columnLevelText = this.t('tnvedSearch.columnLevel', this.columnLevelText);
    this.columnNameText = this.t('tnvedSearch.columnName', this.columnNameText);
    this.columnSimilarityText = this.t('tnvedSearch.columnSimilarity', this.columnSimilarityText);
    this.levelChapterText = this.t('tnvedSearch.levelChapter', this.levelChapterText);
    this.levelHeadingText = this.t('tnvedSearch.levelHeading', this.levelHeadingText);
    this.emptyText = this.t('tnvedSearch.empty', this.emptyText);
    this.errorText = this.t('tnvedSearch.error', this.errorText);
    this.tabCategoriesText = this.t('tnvedSearch.tabCategories', this.tabCategoriesText);
    this.tabCodeText = this.t('tnvedSearch.tabCode', this.tabCodeText);
    this.subtitleCodeText = this.t('tnvedSearch.subtitleCode', this.subtitleCodeText);
    this.nameLabelText = this.t('tnvedSearch.nameLabel', this.nameLabelText);
    this.descriptionLabelText = this.t('tnvedSearch.descriptionLabel', this.descriptionLabelText);
    this.descriptionPlaceholderText = this.t('tnvedSearch.descriptionPlaceholder', this.descriptionPlaceholderText);
    this.resolveText = this.t('tnvedSearch.resolve', this.resolveText);
    this.resolvingText = this.t('tnvedSearch.resolving', this.resolvingText);
    this.resultCodeText = this.t('tnvedSearch.resultCode', this.resultCodeText);
    this.resultProductText = this.t('tnvedSearch.resultProduct', this.resultProductText);
    this.sourceAiText = this.t('tnvedSearch.sourceAi', this.sourceAiText);
    this.sourceTreeText = this.t('tnvedSearch.sourceTree', this.sourceTreeText);
    this.notFullText = this.t('tnvedSearch.notFull', this.notFullText);
    this.notFoundText = this.t('tnvedSearch.notFound', this.notFoundText);
    this.hintsTitleText = this.t('tnvedSearch.hintsTitle', this.hintsTitleText);
    this.refreshText = this.t('tnvedSearch.refresh', this.refreshText);
    this.cachedText = this.t('tnvedSearch.cached', this.cachedText);
  }
}
